import React, { useEffect, useState } from "react";
import {
  ChoiceGroup,
  Dropdown,
  FontWeights,
  IconButton,
  IDropdownOption,
  ResponsiveMode,
  TeachingBubble,
  Toggle,
} from "@fluentui/react";
import { TextField } from "@fluentui/react/lib/TextField";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { FormatTypeEnum } from "../../api/models/format-type-enum";
import { FormatPreview } from "../../api/models/format-preview";
import { formatPreview } from "../../api/document/Format";
import { AdvancedDocumentMetadata } from "../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import {
  getDefaultBuiltInFormattingString,
  getDefaultCustomFormattingString,
} from "../../utils/helpers/ObjectFormattingHelper";
import { builtInDateFormat, builtInNumberFormat, builtInPercentageFormat } from "../../utils/constants/formats";
import { ImageDropdown } from "../coobjects/ImageDropdown";
import { DocumentParameters } from "./ParametersDocument";
import { getTemplates } from "../../api/document/Templates";
import { optionsLocale } from "../../utils/constants/locale";
import { DropdownSearch } from "../inputs/DropdownSearch";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  selectIxbrlSettings,
  selectServiceProvider,
  selectUserFunctions,
  setIxbrlEnabled,
  setIxbrlSettings,
} from "../../redux/feature/settings/configurations-slice";
import { ServiceProvider } from "../../api/models/service-provider";
import {
  fetchIxbrlSettings,
  fetchUpdateIxbrlSettings,
  hasNoDefaultServiceProvider,
} from "../../pages/seahorse/SeahorseUtils";
import { store } from "../../redux/store/store";

const validate = (input: AdvancedDocumentMetadata) => {
  return !!(input.numberFormat?.length && input.percentageFormat?.length && input.dateFormat?.length);
};

interface DocumentAdvancedProps {
  documentId?: string;
  documentAdvanced: AdvancedDocumentMetadata;
  updateDocumentAdvanced: (doc: AdvancedDocumentMetadata, valid: boolean) => void;
  isEdit: boolean;
  parameters: DocumentParameters[];
  goToParametersPage: () => void;
  isDuplicate: boolean;
}

interface PreviewState {
  previewNumber: string;
  previewPercentage: string;
  previewDate: string;
}

export interface NumberState {
  typeNumber: string;
  numberFormat: string;
}

export interface PercentageState {
  typePercentage: string;
  percentageFormat: string;
}

export interface DateState {
  typeDate: string;
  dateFormat: string;
}

export interface InfoState {
  teachingBubbleVisible: boolean;
}

const DEFAULT_VALUE = "1234567.89";
const DEFAULT_DATE = "01/01/2000";
const TYPE_DEFAULT = "Default";
const TYPE_CUSTOM = "Custom";

export const AdvancedDocumentPanel: React.FunctionComponent<DocumentAdvancedProps> = (props) => {
  const [state, setState] = useState<PreviewState>({
    previewNumber: "",
    previewPercentage: "",
    previewDate: "",
  });

  const { documentId, documentAdvanced, updateDocumentAdvanced, goToParametersPage } = props;

  const [stateNumber, setStateNumber] = useState<NumberState>({
    typeNumber: documentAdvanced.typeNumber ?? TYPE_CUSTOM,
    numberFormat: documentAdvanced.numberFormat ?? "",
  });

  const [statePercentage, setStatePercentage] = useState<PercentageState>({
    typePercentage: documentAdvanced.typePercentage ?? TYPE_CUSTOM,
    percentageFormat: documentAdvanced.percentageFormat ?? "",
  });

  const [stateDate, setStateDate] = useState<DateState>({
    typeDate: documentAdvanced.typeDate ?? TYPE_CUSTOM,
    dateFormat: documentAdvanced.dateFormat ?? "",
  });

  const [currentTeachingBubble, setTeachingBubble] = useState<"image" | "format" | "template">(null);

  const [templates, setTemplates] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    getInitialFormatPreview();
    const fetchAndSetTemplates = async () => {
      try {
        const fetchedTemplates = await getTemplates();
        const templateOptions = fetchedTemplates.map((t) => ({
          key: t.templateId,
          text: t.templateName,
        }));

        templateOptions.push({
          key: "default",
          text: formatMessage("cof.newdocument.template.default"),
        });

        // If templateInfo exists, ensure it is in the list
        if (documentAdvanced.templateInfo) {
          const selectedTemplate = templateOptions.find((t) => t.key === documentAdvanced.templateInfo.templateId);
          if (!selectedTemplate) {
            templateOptions.unshift({
              key: documentAdvanced.templateInfo.templateId,
              text: documentAdvanced.templateInfo.templateName,
            });
          }
        }

        setTemplates(templateOptions);
      } catch (error) {
        // eslint-disable-next-line no-undef
        console.error("Error fetching templates:", error);
        setTemplates([{ key: "default", text: formatMessage("cof.newdocument.template.default") }]);
      }
    };

    if (!props.isEdit || props.isDuplicate || !templates.length) {
      fetchAndSetTemplates();
    }
  }, [props.isEdit, props.isDuplicate]); // Keep the dependency array minimal

  const getInitialFormatPreview = async (refreshProps?: {
    locale: string;
    stateNumber: NumberState;
    statePercentage: PercentageState;
    stateDate: DateState;
  }) => {
    let locale;
    let numberFormatProps: NumberState;
    let percentageFormatProps: PercentageState;
    let dateFormatProps: DateState;

    if (refreshProps) {
      locale = refreshProps.locale;
      numberFormatProps = refreshProps.stateNumber;
      percentageFormatProps = refreshProps.statePercentage;
      dateFormatProps = refreshProps.stateDate;
    } else {
      locale = documentAdvanced.locale;
      numberFormatProps = {
        numberFormat: documentAdvanced.numberFormat ?? getDefaultCustomFormattingString(FormatTypeEnum.NUMBER),
        typeNumber: documentAdvanced.typeNumber ?? TYPE_CUSTOM,
      };
      percentageFormatProps = {
        percentageFormat:
          documentAdvanced.percentageFormat ?? getDefaultCustomFormattingString(FormatTypeEnum.PERCENTAGE),
        typePercentage: documentAdvanced.typePercentage ?? TYPE_CUSTOM,
      };
      dateFormatProps = {
        dateFormat: documentAdvanced.dateFormat ?? getDefaultCustomFormattingString(FormatTypeEnum.DATE),
        typeDate: documentAdvanced.typeDate ?? TYPE_CUSTOM,
      };
    }

    const number: FormatPreview = {
      value: DEFAULT_VALUE,
      format: numberFormatProps.numberFormat,
      typeFormatChoice: numberFormatProps.typeNumber,
      typeFormat: FormatTypeEnum.NUMBER,
      locale,
    };
    const percentage: FormatPreview = {
      value: DEFAULT_VALUE,
      format: percentageFormatProps.percentageFormat,
      typeFormatChoice: percentageFormatProps.typePercentage,
      typeFormat: FormatTypeEnum.PERCENTAGE,
      locale,
    };
    const date: FormatPreview = {
      value: DEFAULT_DATE,
      format: dateFormatProps.dateFormat,
      typeFormatChoice: dateFormatProps.typeDate,
      typeFormat: FormatTypeEnum.DATE,
      locale,
    };
    const initialState: PreviewState = {
      previewNumber: "",
      previewPercentage: "",
      previewDate: "",
    };
    const promises: Promise<void>[] = [
      formatPreview(number).then((v) => {
        initialState.previewNumber = v;
      }),
      formatPreview(percentage).then((v) => {
        initialState.previewPercentage = v;
      }),
      formatPreview(date).then((v) => {
        initialState.previewDate = v;
      }),
    ];
    await Promise.all(promises);
    setState(initialState);
  };

  const optionsChoice = [
    { key: TYPE_DEFAULT, text: formatMessage("cof.base.default") },
    { key: TYPE_CUSTOM, text: formatMessage("cof.base.custom") },
  ];

  const onChangeNumber = (value, typeNumber) => {
    const newDocAdvanced: AdvancedDocumentMetadata = {
      ...documentAdvanced,
      numberFormat: value,
      typeNumber: typeNumber,
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStateNumber((s) => ({ ...s, numberFormat: value, typeNumber: typeNumber }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_VALUE,
        format: value,
        typeFormatChoice: typeNumber,
        locale: documentAdvanced.locale,
        typeFormat: FormatTypeEnum.NUMBER,
      }).then((previewN) => setState((s) => ({ ...s, previewNumber: previewN })));
    } else {
      setState((s) => ({ ...s, previewNumber: "" }));
    }
  };

  const onChangeTemplate = (choice: IDropdownOption) => {
    const newDocAdvanced: AdvancedDocumentMetadata = {
      ...documentAdvanced,
      templateInfo: { templateId: choice.key.toString(), templateName: choice.text },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
  };

  const onChangeLocale = (choice: IDropdownOption) => {
    const newLocale = choice.key.toString();

    const newDocAdvanced: AdvancedDocumentMetadata = {
      ...documentAdvanced,
      locale: newLocale,
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    getInitialFormatPreview({ locale: newLocale, stateNumber, statePercentage, stateDate });
  };

  const onChangePercentage = (value, typePercentage) => {
    const newDocAdvanced: AdvancedDocumentMetadata = {
      ...documentAdvanced,
      percentageFormat: value,
      typePercentage: typePercentage,
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStatePercentage((s) => ({ ...s, percentageFormat: value, typePercentage: typePercentage }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_VALUE,
        format: value,
        typeFormatChoice: typePercentage,
        locale: documentAdvanced.locale,
        typeFormat: FormatTypeEnum.PERCENTAGE,
      }).then((previewN) => setState((s) => ({ ...s, previewPercentage: previewN })));
    } else {
      setState((s) => ({ ...s, previewPercentage: "" }));
    }
  };

  const onChangeDate = (value, typeDate) => {
    const newDocAdvanced: AdvancedDocumentMetadata = {
      ...documentAdvanced,
      dateFormat: value,
      typeDate: typeDate,
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStateDate((s) => ({ ...s, dateFormat: value, typeDate: typeDate }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_DATE,
        format: value,
        typeFormatChoice: typeDate,
        locale: documentAdvanced.locale,
        typeFormat: FormatTypeEnum.DATE,
      }).then((previewD) => setState((s) => ({ ...s, previewDate: previewD })));
    } else {
      setState((s) => ({ ...s, previewDate: "" }));
    }
  };

  const handleChangeImageExtension = (selectedKey) => {
    const newDocAdvanced: AdvancedDocumentMetadata = { ...documentAdvanced, imageExtension: selectedKey };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
  };

  const handleChangeImageResolution = (selectedKey) => {
    const newDocAdvanced: AdvancedDocumentMetadata = { ...documentAdvanced, imageResolution: selectedKey };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
  };

  const customToggleStyles = {
    label: {
      fontWeight: FontWeights.regular,
      display: "inline-block",
      marginRight: "5px",
    },
    container: {
      display: "inline-block",
    },
  };

  const handleIXBRLToggle = (isChecked) => {
    setIsIxbrlSettingsAvailable(isChecked);
    if (documentId) {
      fetchUpdateIxbrlSettings(documentId, isChecked).then(() => {
        fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
      });
    } else {
      store.dispatch(setIxbrlEnabled(isChecked));
    }
  };

  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const notAuthorizedUserForIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions);
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const allIxbrlSettings = useAppSelector(selectIxbrlSettings);
  let [isIxbrlSettingsAvailable, setIsIxbrlSettingsAvailable] = useState(false);

  useEffect(() => {
    if (allIxbrlSettings && Array.isArray(allIxbrlSettings)) {
      allIxbrlSettings.forEach((setting) => {
        if (documentId === setting.documentId) {
          setIsIxbrlSettingsAvailable(setting.isChecked);
        }
      });
    }
  }, [allIxbrlSettings]);

  const infoId = "AdvancedDocumentPanelInfoId";
  const imageFormatInfoId = "AdvancedDocumentPanelImageFormatInfoId";
  const templateInfoId = "AdvancedDocumentPanelTemplateInfoId";
  let dimensions = props.parameters?.filter((parameterObject) => parameterObject.dimensions?.length > 0);
  return (
    <>
      {!props.isEdit && dimensions.length == 0 ? (
        <div
          style={{
            height: "30px",
            lineHeight: "15px",
            backgroundColor: "darkorange",
            fontSize: "0.8rem",
            color: "white",
            marginTop: "0.7rem",
            marginBottom: "0.7rem",
            padding: "0.5rem 0.5rem 0.5rem 0.5rem",
            borderRadius: "5px",
          }}
        >
          <img
            alt="warning"
            src={"/assets/warning-white.svg"}
            style={{
              justifyContent: "left",
              width: "15px",
              height: "15px",
              objectFit: "contain",
            }}
          />
          <span
            style={{
              marginLeft: "0.5rem",
            }}
          >
            {formatMessage("cof.dimensions.notconfigured")}
          </span>
          <span
            style={{
              float: "right",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={goToParametersPage}
          >
            {formatMessage("cof.base.configure")}
          </span>
        </div>
      ) : null}
      {!notAuthorizedUserForIXBRL && (
        <>
          <div className="ixbrlToggle">{"iXBRL"}</div>
          <div style={{ display: "inline-flex", float: "right" }}>
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ marginTop: "5px" }}>
              <Toggle
                styles={customToggleStyles}
                checked={isIxbrlSettingsAvailable}
                disabled={hasNoDefaultServiceProvider(defaultServiceProvider)}
                onChange={(_event, isIxbrlSettingsAvailable?: boolean) => handleIXBRLToggle(isIxbrlSettingsAvailable)}
              />
            </div>
          </div>
        </>
      )}
      {
        // TODO as described into IDS-1387
        <>
          {currentTeachingBubble === "template" && (
            <TeachingBubble
              calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
              target={`#${templateInfoId}`}
              hasCloseButton={true}
              closeButtonAriaLabel="Close"
              onDismiss={() => setTeachingBubble(null)}
              headline={formatMessage("cof.createnewdocument.template.info.title")}
            >
              <text>{formatMessage("cof.createnewdocument.template.info.message")}</text>
            </TeachingBubble>
          )}
          <div
            style={{
              fontSize: "1rem",
              fontWeight: "500",
              lineHeight: "1.25rem",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: "var(--color-blue)",
            }}
          >
            {formatMessage("cof.newdocument.templateoptions")}
            <IconButton
              id={templateInfoId}
              aria-label="Info"
              iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
              onClick={() => setTeachingBubble("template")}
              data-testid={templateInfoId}
            />
          </div>
          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                <Dropdown
                  data-testid={"templates-input"}
                  options={templates}
                  placeholder={formatMessage("cof.newdocument.template.default")}
                  onChange={(_event, option) => {
                    onChangeTemplate(option);
                  }}
                  selectedKey={documentAdvanced.templateInfo?.templateId}
                  responsiveMode={ResponsiveMode.large}
                  disabled={props.isEdit}
                  label={formatMessage("cof.base.template")}
                />
              </div>
            </div>
          </div>
        </>
      }
      {currentTeachingBubble === "format" && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setTeachingBubble(null)}
          headline={formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.title")}
        >
          <text>{formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.base.formatoptions")}
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setTeachingBubble("format")}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <DropdownSearch
              data-testid={"locale-input"}
              options={optionsLocale}
              placeholder={formatMessage("cof.base.select")}
              onChange={(_event, option) => {
                onChangeLocale(option);
              }}
              selectedKey={documentAdvanced.locale}
              responsiveMode={ResponsiveMode.large}
              label={formatMessage("cof.base.locale")}
            />
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={stateNumber.typeNumber}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              onChange={(_, choice) => {
                onChangeNumber(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.NUMBER)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.number")}
            />
            {stateNumber.typeNumber === TYPE_DEFAULT && (
              <Dropdown
                data-testid={"number-default"}
                options={builtInNumberFormat}
                onChange={(_event, option) => {
                  onChangeNumber(option.key, stateNumber.typeNumber);
                }}
                selectedKey={
                  stateNumber.numberFormat ? builtInNumberFormat.find((f) => f.key == stateNumber.numberFormat)?.key : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {stateNumber.typeNumber === TYPE_CUSTOM && (
              <TextField
                value={stateNumber.numberFormat ?? ""}
                onChange={(e) => onChangeNumber((e.target as HTMLInputElement)?.value ?? "", stateNumber.typeNumber)}
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewNumber}</div>
            </div>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={statePercentage.typePercentage}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              onChange={(_, choice) => {
                onChangePercentage(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.PERCENTAGE)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.PERCENTAGE),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.percentage")}
            />
            {statePercentage.typePercentage === TYPE_DEFAULT && (
              <Dropdown
                data-testid={"percentage-default"}
                options={builtInPercentageFormat}
                onChange={(_event, option) => {
                  onChangePercentage(option.key, statePercentage.typePercentage);
                }}
                selectedKey={
                  statePercentage.percentageFormat
                    ? builtInPercentageFormat.find((f) => f.key == statePercentage.percentageFormat)?.key
                    : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {statePercentage.typePercentage == TYPE_CUSTOM && (
              <TextField
                value={statePercentage.percentageFormat ?? ""}
                onChange={(e) =>
                  onChangePercentage((e.target as HTMLInputElement)?.value ?? "", statePercentage.typePercentage)
                }
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewPercentage}</div>
            </div>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={stateDate.typeDate}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              onChange={(_, choice) => {
                onChangeDate(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.DATE)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.DATE),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.date")}
            />
            {stateDate.typeDate == TYPE_DEFAULT && (
              <Dropdown
                data-testid={"date-default"}
                options={builtInDateFormat}
                onChange={(_event, option) => {
                  onChangeDate(option.key, stateDate.typeDate);
                }}
                selectedKey={
                  stateDate.dateFormat ? builtInDateFormat.find((f) => f.key == stateDate.dateFormat)?.key : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {stateDate.typeDate == TYPE_CUSTOM && (
              <TextField
                value={stateDate.dateFormat ?? ""}
                onChange={(e) => onChangeDate((e.target as HTMLInputElement)?.value ?? "", stateDate.typeDate)}
              />
            )}
            <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewDate}</div>
          </div>
        </div>
      </div>

      {currentTeachingBubble === "image" && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${imageFormatInfoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setTeachingBubble(null)}
          headline={formatMessage("cof.createnewdocument.imagesformatting.info.title")}
        >
          <text>{formatMessage("cof.createnewdocument.imagesformatting.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.createnewdocument.imagesformatting")}
        <IconButton
          id={imageFormatInfoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setTeachingBubble("image")}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <ImageDropdown
            imageExtension={documentAdvanced.imageExtension}
            imageResolution={documentAdvanced.imageResolution}
            disabled={false}
            required={false}
            onExtensionChange={handleChangeImageExtension}
            onResolutionChange={handleChangeImageResolution}
          />
        </div>
      </div>
    </>
  );
};
